import React, { useState, useEffect, useMemo, useContext } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import {
  Warehouse,
  Plus,
  Save,
  Pencil,
} from "lucide-react";

const API_BASE = "http://nissi-india.com/api";

const emptyForm = {
  storeName: "",
  code: "",
  isMainStore: false,
  address1: "",
  address2: "",
  country: "",
  state: "",
  district: "",
  city: "",
  zipcode: "",
  contactPerson: "",
  email: "",
  phone: "",
  mobile: "",
  remarks: "",
};

const StoreMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const axiosConfig = useMemo(
    () => ({ headers: commonHeaders }),
    [commonHeaders]
  );

  const [formData, setFormData] = useState(emptyForm);
  const [isMainStoreDisabled, setIsMainStoreDisabled] = useState(false);
  const [storeList, setStoreList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  useEffect(() => {
    if (!branchId || !userId) {
      toast.error("Branch ID or User ID missing in localStorage");
      return;
    }
    fetchInitialData();
  }, [branchId, userId, axiosConfig]);

  const fetchInitialData = async () => {
    await Promise.all([
      generateStoreCode(),
      fetchCountries(),
      fetchStores(),
      checkMainStore(),
    ]);
  };

  const generateStoreCode = async () => {
    try {
      const res = await axios.get(`${API_BASE}/generateAndCheckStoreCode`, {
        params: { loginuserid: userId },
        ...axiosConfig,
      });
      setFormData((prev) => ({
        ...prev,
        code: res.data?.storecode || "",
      }));
    } catch (err) {
      console.error("generateStoreCode error:", err);
      toast.error("Failed to generate store code");
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bindCountry`, axiosConfig);
      if (Array.isArray(res.data?.countryList)) {
        setCountries(res.data.countryList);
      } else {
        setCountries([]);
        toast.error("Invalid country response format");
      }
    } catch (err) {
      console.error("Country fetch error:", err);
      toast.error("Error fetching countries");
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/stateDropdownChanged`, {
        params: { countryid: countryId },
        ...axiosConfig,
      });
      if (Array.isArray(res.data?.stateList)) {
        setStates(res.data.stateList);
      } else {
        setStates([]);
      }
    } catch (err) {
      console.error("State fetch error:", err);
      toast.error("Error fetching states");
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/distDropdownChanged`, {
        params: { stateid: stateId },
        ...axiosConfig,
      });
      if (Array.isArray(res.data?.distList)) {
        setDistricts(res.data.distList);
      } else {
        setDistricts([]);
      }
    } catch (err) {
      console.error("District fetch error:", err);
      toast.error("Error fetching districts");
    }
  };

  const fetchCities = async (distId) => {
    if (!distId) {
      setCities([]);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/cityDropdownChanged`, {
        params: { distid: distId },
        ...axiosConfig,
      });
      if (Array.isArray(res.data?.cityList)) {
        setCities(res.data.cityList);
      } else {
        setCities([]);
      }
    } catch (err) {
      console.error("City fetch error:", err);
      toast.error("Error fetching cities");
    }
  };

  const checkMainStore = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/checkMainStore/${branchId}`,
        axiosConfig
      );

      if (String(branchId) === "1") {
        setIsMainStoreDisabled(true);
        setFormData((prev) => ({ ...prev, isMainStore: true }));
        return;
      }

      const exists = res.data?.isMainStoreExists || false;
      const enabled = res.data?.isMainStoreCheckboxEnabled ?? true;

      setIsMainStoreDisabled(!enabled);

      if (exists) {
        toast.info("Main store already exists for this branch.");
      }
    } catch (err) {
      console.error("Main store check failed:", err);
      toast.error("Failed to check main store");
    }
  };

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API_BASE}/storeMasterList`, {
        params: { branchid: branchId, page: 1, pageSize: 50, search: "" },
        ...axiosConfig,
      });
      const list = Array.isArray(res.data?.storeList) ? res.data.storeList : [];
      setStoreList(list);
    } catch (err) {
      console.error("fetchStores error:", err);
      toast.error("Failed to load store list");
      setStoreList([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCountryChange = async (e) => {
    const countryId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      country: countryId,
      state: "",
      district: "",
      city: "",
    }));
    setStates([]);
    setDistricts([]);
    setCities([]);
    await fetchStates(countryId);
  };

  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state: stateId,
      district: "",
      city: "",
    }));
    setDistricts([]);
    setCities([]);
    await fetchDistricts(stateId);
  };

  const handleDistrictChange = async (e) => {
    const districtId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      district: districtId,
      city: "",
    }));
    setCities([]);
    await fetchCities(districtId);
  };

  const handleAddNew = async () => {
    setFormData(emptyForm);
    setStates([]);
    setDistricts([]);
    setCities([]);
    setIsEditing(false);
    setEditingStoreId(null);
    setShowForm(true);
    await generateStoreCode();
    await checkMainStore();
  };

  const handleEdit = async (store) => {
    setIsEditing(true);
    setEditingStoreId(store.storeid);
    setShowForm(true);

    setFormData({
      storeName: store.storename || "",
      code: store.storecode || "",
      isMainStore: !!store.ismainstore,
      address1: store.address1 || "",
      address2: store.address2 || "",
      country: store.countryid ? String(store.countryid) : "",
      state: store.stateid ? String(store.stateid) : "",
      district: store.districtid ? String(store.districtid) : "",
      city: store.cityid ? String(store.cityid) : "",
      zipcode: store.zipcode || "",
      contactPerson: store.contactperson || "",
      email: store.email || "",
      phone: store.phoneno || "",
      mobile: store.mobile || "",
      remarks: store.remarks || "",
    });

    if (store.countryid) await fetchStates(store.countryid);
    if (store.stateid) await fetchDistricts(store.stateid);
    if (store.districtid) await fetchCities(store.districtid);
  };

  const validateForm = () => {
    if (!formData.storeName.trim()) return "Store Name is required";
    if (!formData.code.trim()) return "Code is required";
    if (!formData.address1.trim()) return "Address 1 is required";
    if (!formData.country) return "Country is required";
    if (!formData.state) return "State is required";
    if (!formData.contactPerson.trim()) return "Contact Person is required";
    if (!formData.zipcode.trim()) return "Zipcode is required";
    if (!formData.phone.trim()) return "Phone is required";
    return "";
  };

  const handleSubmit = async () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const payload = {
      storename: formData.storeName,
      storecode: formData.code,
      contactperson: formData.contactPerson,
      address1: formData.address1,
      address2: formData.address2,
      cityid: formData.city || 0,
      stateid: formData.state,
      countryid: formData.country,
      distid: formData.district || 0,
      zipcode: formData.zipcode,
      phone: formData.phone,
      mobile: formData.mobile,
      email: formData.email,
      remarks: formData.remarks,
      ismain: formData.isMainStore,
      branchid: branchId,
      loginuserid: userId,
    };

    try {
      setLoading(true);

      if (isEditing && editingStoreId) {
        await axios.put(
          `${API_BASE}/updateStore/${editingStoreId}`,
          payload,
          axiosConfig
        );
        toast.success("Store updated successfully");
      } else {
        await axios.post(`${API_BASE}/insertStore`, payload, axiosConfig);
        toast.success("Store added successfully");
      }

      await fetchStores();
      await handleAddNew();
    } catch (err) {
      console.error("handleSubmit error:", err);
      toast.error(err?.response?.data?.message || "Failed to save store");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Store Master</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Warehouse className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {isEditing ? "Edit Store" : "Add New Store"}
                </h3>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {isEditing ? "Update store details" : "Create a new store location"}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowForm(!showForm)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {showForm ? "Hide Form" : "Show Form"}
            </button>
          </div>

          {showForm && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {/* Left Column */}
                <div className="space-y-5">
                  <div>
                    <label className={labelClass}>
                      Store Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="storeName"
                      className={inputClass}
                      value={formData.storeName}
                      onChange={handleChange}
                      placeholder="Enter store name"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Code</label>
                    <input
                      name="code"
                      readOnly
                      className={`${inputClass} opacity-70 cursor-not-allowed`}
                      value={formData.code}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="isMainStore"
                      id="isMainStore"
                      checked={formData.isMainStore}
                      onChange={handleChange}
                      disabled={isMainStoreDisabled}
                      className="w-4 h-4 rounded"
                    />
                    <label
                      htmlFor="isMainStore"
                      className={`text-sm font-medium ${
                        isDark ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Is Main Store
                    </label>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Address 1 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="address1"
                      className={`${inputClass} resize-none`}
                      rows={2}
                      value={formData.address1}
                      onChange={handleChange}
                      placeholder="Address line 1"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Address 2</label>
                    <textarea
                      name="address2"
                      className={`${inputClass} resize-none`}
                      rows={2}
                      value={formData.address2}
                      onChange={handleChange}
                      placeholder="Address line 2"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="country"
                      className={inputClass}
                      value={formData.country}
                      onChange={handleCountryChange}
                    >
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option key={c.fld_countryid} value={c.fld_countryid}>
                          {c.fld_countryname}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="state"
                      className={inputClass}
                      value={formData.state}
                      onChange={handleStateChange}
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s.fld_stateid} value={s.fld_stateid}>
                          {s.fld_statename}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>District</label>
                    <select
                      name="district"
                      className={inputClass}
                      value={formData.district}
                      onChange={handleDistrictChange}
                    >
                      <option value="">Select District</option>
                      {districts.map((d) => (
                        <option key={d.fld_districtid} value={d.fld_districtid}>
                          {d.fld_districtname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  <div>
                    <label className={labelClass}>City</label>
                    <select
                      name="city"
                      className={inputClass}
                      value={formData.city}
                      onChange={handleChange}
                    >
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c.fld_cityid} value={c.fld_cityid}>
                          {c.fld_cityname}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Zipcode <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="zipcode"
                      className={inputClass}
                      value={formData.zipcode}
                      onChange={handleChange}
                      placeholder="Postal / Zip code"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="contactPerson"
                      className={inputClass}
                      value={formData.contactPerson}
                      onChange={handleChange}
                      placeholder="Contact person name"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      name="email"
                      type="email"
                      className={inputClass}
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="phone"
                      className={inputClass}
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Mobile</label>
                    <input
                      name="mobile"
                      className={inputClass}
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Mobile number"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Remarks</label>
                    <textarea
                      name="remarks"
                      className={`${inputClass} resize-none`}
                      rows={3}
                      value={formData.remarks}
                      onChange={handleChange}
                      placeholder="Optional remarks..."
                    />
                  </div>
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={handleAddNew}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Plus size={16} />
                  Add New
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  <Save size={16} />
                  {loading
                    ? "Saving..."
                    : isEditing
                    ? "Update Store"
                    : "Save Store"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Store List</h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                }`}
              >
                {storeList.length} records
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Store Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Address 1</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Address 2</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Edit</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {storeList.length > 0 ? (
                  storeList.map((item, idx) => (
                    <tr
                      key={item.storeid || idx}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium">{item.storename}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {item.storecode}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.address1 || "-"}</td>
                      <td className="px-4 py-3">{item.address2 || "-"}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Warehouse size={40} className="text-gray-400" />
                        <span>No stores found</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default StoreMaster;